import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import useSessionStore from "./hooks/useSessionStore";
import {
  ApiEnvelope,
  callElevator,
  createSession,
  ElevatorRealtime,
  ElevatorUnit,
  fetchHistory,
  fetchRealtime,
  HistoryEntry,
  listElevators,
  openDoor
} from "./services/openApiService";

const WORK_MODE_LABELS: Record<string, string> = {
  UNKNOWN: "未知",
  DEFAULT: "默认",
  STANDALONE: "独立模式",
  MAINTENANCE: "维保模式"
};

const DIRECTION_LABELS: Record<string, string> = {
  UNKNOWN: "未知",
  BIDIRECTION: "双向待命",
  MOVE_UP: "上行",
  MOVE_DOWN: "下行"
};

const DOOR_STATE_LABELS: Record<string, string> = {
  UNKNOWN: "未知",
  DOOR_OPENING: "开门中",
  DOOR_OPENED: "开门到位",
  DOOR_CLOSING: "关门中",
  DOOR_CLOSED: "关门到位"
};

const ONLINE_LABELS: Record<string, string> = {
  ONLINE: "在线",
  OFFLINE: "离线"
};

const VIBRATION_LABELS: Record<number, string> = {
  0: "无",
  1: "弱",
  2: "强"
};

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : "请求失败";

const parseSiteUID = (value: string): number | undefined => {
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const normalizeElevators = (data: unknown): ElevatorUnit[] => {
  if (Array.isArray(data)) {
    return data
      .map((item) => asRecord(item))
      .filter((item): item is Record<string, unknown> => Boolean(item))
      .map((item) => ({
        unit_uid: Number(item.unit_uid),
        unit_name:
          typeof item.unit_name === "string" ? item.unit_name : undefined,
        unit_type:
          typeof item.unit_type === "string" ? item.unit_type : undefined,
        unit_sn: typeof item.unit_sn === "string" ? item.unit_sn : undefined
      }))
      .filter((item) => Number.isFinite(item.unit_uid) && item.unit_uid > 0);
  }
  return [];
};

const normalizeRealtime = (data: unknown): ElevatorRealtime[] => {
  const list = Array.isArray(data) ? data : [data];
  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => Boolean(item))
    .map((item) => ({
      ...item,
      unit_uid: Number(item.unit_uid)
    }))
    .filter((item) => Number.isFinite(item.unit_uid) && item.unit_uid > 0);
};

const maskSecret = (secret: string): string => {
  if (secret.length <= 4) {
    return "****";
  }
  return `${"*".repeat(Math.min(secret.length - 4, 8))}${secret.slice(-4)}`;
};

const getPath = (targetURL: string): string => {
  try {
    const parsed = new URL(targetURL);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return targetURL;
  }
};

type ReadableLog = {
  id: string;
  at: string;
  summary: string;
  detail: string;
  ok: boolean;
};

const humanizeLog = (entry: HistoryEntry): ReadableLog => {
  const path = getPath(entry.target_url);
  const req = asRecord(entry.request_body);
  const meta = asRecord(entry.metadata);

  let summary = `${entry.method} ${path}`;
  if (path.includes("/auth/token")) {
    summary = "获取 Token";
  } else if (path.includes("/unit/elevator/open_door")) {
    summary = `开门指令 | 电梯 ${req?.unit_uid ?? "-"} | 楼层 ${req?.floor_index ?? "-"}`;
  } else if (path.includes("/unit/elevator/call")) {
    summary = `呼梯指令 | 电梯 ${req?.unit_uid ?? "-"} | 楼层 ${req?.floor_index ?? "-"}`;
  } else if (path.includes("/unit/elevator/realtime_state")) {
    const count = Array.isArray(req?.unit_uid_list) ? req.unit_uid_list.length : 0;
    summary = `实时状态查询 | ${count || 1} 台电梯`;
  } else if (meta?.action === "list_elevators" || path.includes("/unit/elevator?site_uid=")) {
    summary = "查询站点全部电梯";
  }

  const ok = entry.status_code >= 200 && entry.status_code < 300 && !entry.error;
  const detail = entry.error
    ? `失败: ${entry.error}`
    : `状态 ${entry.status_code} · 耗时 ${entry.latency_ms} ms`;

  return {
    id: entry.id,
    at: new Date(entry.timestamp).toLocaleString(),
    summary,
    detail,
    ok
  };
};

function App() {
  const {
    appId,
    appSecret,
    token,
    expiresAt,
    credentialHistory,
    setCredentials,
    setToken,
    clear,
    rememberCredential,
    useCredential,
    clearCredentialHistory
  } = useSessionStore((state) => ({
    appId: state.appId,
    appSecret: state.appSecret,
    token: state.token,
    expiresAt: state.expiresAt,
    credentialHistory: state.credentialHistory,
    setCredentials: state.setCredentials,
    setToken: state.setToken,
    clear: state.clear,
    rememberCredential: state.rememberCredential,
    useCredential: state.useCredential,
    clearCredentialHistory: state.clearCredentialHistory
  }));
  const [siteUIDInput, setSiteUIDInput] = useState("699");
  const [elevators, setElevators] = useState<ElevatorUnit[]>([]);
  const [selectedUnitUID, setSelectedUnitUID] = useState<number | null>(null);
  const [realtimeByUnit, setRealtimeByUnit] = useState<
    Record<number, ElevatorRealtime>
  >({});
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isLoadingElevators, setIsLoadingElevators] = useState(false);
  const [isLoadingRealtime, setIsLoadingRealtime] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [realtimeError, setRealtimeError] = useState<string | null>(null);
  const [loopAll, setLoopAll] = useState(false);
  const [loopSeconds, setLoopSeconds] = useState(5);
  const [lastRealtimeEnvelope, setLastRealtimeEnvelope] = useState<
    ApiEnvelope<ElevatorRealtime | ElevatorRealtime[]> | null
  >(null);
  const [lastRealtimeAt, setLastRealtimeAt] = useState<Date | null>(null);
  const [callFloorIndex, setCallFloorIndex] = useState(1);
  const [openFloorIndex, setOpenFloorIndex] = useState(1);
  const [openDuration, setOpenDuration] = useState(0);
  const [callLoading, setCallLoading] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [callMessage, setCallMessage] = useState<string | null>(null);
  const [openMessage, setOpenMessage] = useState<string | null>(null);
  const realtimeBusyRef = useRef(false);

  const sessionStatus = useMemo(() => {
    if (!token) return "未获取 Token";
    if (!expiresAt) return "Token 有效期未知";
    const diff = expiresAt.getTime() - Date.now();
    if (diff <= 0) return "Token 已过期";
    const minutes = Math.floor(diff / 60000);
    return `Token 剩余 ${minutes} 分钟`;
  }, [token, expiresAt]);

  const selectedElevator = useMemo(
    () => elevators.find((item) => item.unit_uid === selectedUnitUID) ?? null,
    [elevators, selectedUnitUID]
  );

  const selectedRealtime = useMemo(
    () => (selectedUnitUID ? realtimeByUnit[selectedUnitUID] : undefined),
    [realtimeByUnit, selectedUnitUID]
  );

  const allUnitUIDs = useMemo(
    () => elevators.map((item) => item.unit_uid).filter((uid) => uid > 0),
    [elevators]
  );

  const siteUID = useMemo(() => parseSiteUID(siteUIDInput), [siteUIDInput]);

  const historyQuery = useQuery<HistoryEntry[], Error>({
    queryKey: ["history-readable"],
    queryFn: () => fetchHistory(200),
    refetchInterval: 8000
  });

  const readableLogs = useMemo(
    () => (historyQuery.data ?? []).map((entry) => humanizeLog(entry)),
    [historyQuery.data]
  );

  const acquireToken = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError(null);
    setIsAuthLoading(true);
    try {
      const response = await createSession({
        app_id: appId.trim(),
        app_secret: appSecret.trim()
      });
      setToken(response.token, response.expires_at);
      rememberCredential(appId, appSecret);
    } catch (error) {
      setAuthError(errorText(error));
    } finally {
      setIsAuthLoading(false);
    }
  };

  const loadElevators = useCallback(async () => {
    if (!token) {
      setDiscoverError("请先获取 Token");
      return;
    }
    setDiscoverError(null);
    setIsLoadingElevators(true);
    try {
      const response = await listElevators({
        token,
        site_uid: siteUID
      });
      const next = normalizeElevators(response.data);
      setElevators(next);
      setSelectedUnitUID((prev) => {
        if (prev && next.some((item) => item.unit_uid === prev)) {
          return prev;
        }
        return next[0]?.unit_uid ?? null;
      });
      if (!next.length) {
        setDiscoverError("没有查询到电梯，请确认 site_uid 是否正确。");
      }
    } catch (error) {
      setDiscoverError(errorText(error));
    } finally {
      setIsLoadingElevators(false);
    }
  }, [siteUID, token]);

  const requestRealtime = useCallback(
    async (unitUIDList: number[]) => {
      if (!token) {
        setRealtimeError("请先获取 Token");
        return;
      }
      const targetUnits = unitUIDList.filter((uid) => uid > 0);
      if (!targetUnits.length) {
        setRealtimeError("请先选择至少一台电梯。");
        return;
      }
      if (realtimeBusyRef.current) {
        return;
      }
      realtimeBusyRef.current = true;
      setRealtimeError(null);
      setIsLoadingRealtime(true);
      try {
        const response = await fetchRealtime({
          token,
          site_uid: siteUID,
          unit_uid_list: targetUnits
        });
        const normalized = normalizeRealtime(response.data);
        setRealtimeByUnit((prev) => {
          const next = { ...prev };
          normalized.forEach((item) => {
            next[item.unit_uid] = item;
          });
          return next;
        });
        setLastRealtimeEnvelope(response);
        setLastRealtimeAt(new Date());
      } catch (error) {
        setRealtimeError(errorText(error));
      } finally {
        realtimeBusyRef.current = false;
        setIsLoadingRealtime(false);
      }
    },
    [siteUID, token]
  );

  useEffect(() => {
    if (!loopAll) {
      return;
    }
    if (!token || !allUnitUIDs.length) {
      setLoopAll(false);
      return;
    }
    void requestRealtime(allUnitUIDs);
    const timer = window.setInterval(() => {
      void requestRealtime(allUnitUIDs);
    }, loopSeconds * 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [allUnitUIDs, loopAll, loopSeconds, requestRealtime, token]);

  const handleCall = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !selectedUnitUID) {
      setCallMessage("请先获取 Token 并选择电梯。");
      return;
    }
    setCallLoading(true);
    setCallMessage(null);
    try {
      const response = await callElevator({
        token,
        site_uid: siteUID,
        unit_uid: selectedUnitUID,
        floor_index: callFloorIndex
      });
      setCallMessage(
        response.code === 0
          ? "呼梯指令下发成功。"
          : `呼梯返回: ${response.msg ?? "未知结果"}`
      );
      void requestRealtime([selectedUnitUID]);
    } catch (error) {
      setCallMessage(errorText(error));
    } finally {
      setCallLoading(false);
    }
  };

  const handleOpenDoor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !selectedUnitUID) {
      setOpenMessage("请先获取 Token 并选择电梯。");
      return;
    }
    setOpenLoading(true);
    setOpenMessage(null);
    try {
      const response = await openDoor({
        token,
        site_uid: siteUID,
        unit_uid: selectedUnitUID,
        floor_index: openFloorIndex,
        duration: openDuration
      });
      setOpenMessage(
        response.code === 0
          ? "开门指令下发成功。"
          : `开门返回: ${response.msg ?? "未知结果"}`
      );
      void requestRealtime([selectedUnitUID]);
    } catch (error) {
      setOpenMessage(errorText(error));
    } finally {
      setOpenLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <section className="top-strip">
        <form className="token-row" onSubmit={acquireToken}>
          <div className="top-meta">
            <div className="panel-title">YOGO 电梯调试台</div>
            <div className="panel-subtitle">token 与设备选择集中在一屏完成</div>
          </div>
          <div className="input-inline">
            <label htmlFor="app-id">App ID</label>
            <input
              id="app-id"
              value={appId}
              placeholder="yogo"
              onChange={(event) =>
                setCredentials(event.target.value, appSecret)
              }
              required
            />
          </div>
          <div className="input-inline">
            <label htmlFor="app-secret">Secret Key</label>
            <input
              id="app-secret"
              value={appSecret}
              type="password"
              placeholder="输入 app_secret"
              onChange={(event) =>
                setCredentials(appId, event.target.value)
              }
              required
            />
          </div>
          <div className="button-row">
            <button className="btn btn-primary" disabled={isAuthLoading}>
              {isAuthLoading ? "获取中..." : "获取 Token"}
            </button>
            <button
              type="button"
              className="btn btn-subtle"
              onClick={() => clear()}
            >
              清除 Token
            </button>
          </div>
          <span className="status-chip">{sessionStatus}</span>
        </form>
        <div className="credential-strip">
          <div className="chip-label">历史凭证</div>
          {credentialHistory.length ? (
            credentialHistory.map((item) => (
              <button
                key={`${item.app_id}-${item.last_used_at}`}
                className="credential-chip"
                onClick={() => useCredential(item)}
                type="button"
                title={`${item.app_id} / ${item.app_secret}`}
              >
                {item.app_id} · {maskSecret(item.app_secret)}
              </button>
            ))
          ) : (
            <span className="empty-tip">暂无历史</span>
          )}
          {credentialHistory.length ? (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => clearCredentialHistory()}
            >
              清空
            </button>
          ) : null}
        </div>
        {authError ? <div className="error-block">{authError}</div> : null}
      </section>

      <div className="main-grid">
        <section className="panel">
          <header className="section-header">
            <div>
              <div className="panel-title">电梯列表</div>
              <div className="panel-subtitle">先查站点下所有电梯，再选中目标电梯</div>
            </div>
            <div className="status-lite">{elevators.length} 台</div>
          </header>
          <div className="toolbar">
            <div className="input-inline compact">
              <label htmlFor="site-uid">Site UID</label>
              <input
                id="site-uid"
                value={siteUIDInput}
                onChange={(event) => setSiteUIDInput(event.target.value)}
                placeholder="699"
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void loadElevators()}
              disabled={isLoadingElevators}
            >
              {isLoadingElevators ? "查询中..." : "查询所有电梯"}
            </button>
          </div>
          {discoverError ? <div className="error-block">{discoverError}</div> : null}
          <div className="elevator-list">
            {elevators.length ? (
              elevators.map((item) => {
                const realtime = realtimeByUnit[item.unit_uid];
                return (
                  <button
                    type="button"
                    key={item.unit_uid}
                    className={`elevator-item ${
                      item.unit_uid === selectedUnitUID ? "active" : ""
                    }`}
                    onClick={() => setSelectedUnitUID(item.unit_uid)}
                  >
                    <div className="elevator-row">
                      <strong>{item.unit_name || `电梯 ${item.unit_uid}`}</strong>
                      <span>UID {item.unit_uid}</span>
                    </div>
                    <div className="elevator-row muted">
                      <span>{item.unit_type || "ELEVATOR"}</span>
                      <span>SN {item.unit_sn || "-"}</span>
                    </div>
                    <div className="elevator-row muted">
                      <span>楼层 {realtime?.current_floor_name ?? "-"}</span>
                      <span>
                        门状态{" "}
                        {DOOR_STATE_LABELS[realtime?.current_door_state ?? ""] ||
                          realtime?.current_door_state ||
                          "-"}
                      </span>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="empty-tip">还没有电梯数据，先点击“查询所有电梯”。</div>
            )}
          </div>
        </section>

        <div className="column-stack">
          <section className="panel">
            <header className="section-header">
              <div>
                <div className="panel-title">实时快照</div>
                <div className="panel-subtitle">支持单电梯查询和循环查询全部电梯</div>
              </div>
              <div className="status-lite">
                {lastRealtimeAt
                  ? `更新于 ${lastRealtimeAt.toLocaleTimeString()}`
                  : "暂无快照"}
              </div>
            </header>
            <div className="toolbar">
              <button
                type="button"
                className="btn btn-subtle"
                onClick={() =>
                  selectedUnitUID ? void requestRealtime([selectedUnitUID]) : undefined
                }
                disabled={!selectedUnitUID || isLoadingRealtime}
              >
                查询当前电梯
              </button>
              <button
                type="button"
                className="btn btn-subtle"
                onClick={() => void requestRealtime(allUnitUIDs)}
                disabled={!allUnitUIDs.length || isLoadingRealtime}
              >
                查询全部电梯
              </button>
              <div className="loop-inline">
                <select
                  value={loopSeconds}
                  onChange={(event) =>
                    setLoopSeconds(Number.parseInt(event.target.value, 10))
                  }
                >
                  {[3, 5, 10, 15].map((sec) => (
                    <option value={sec} key={sec}>
                      {sec}s
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`btn ${loopAll ? "btn-danger" : "btn-primary"}`}
                  onClick={() => setLoopAll((prev) => !prev)}
                  disabled={!allUnitUIDs.length}
                >
                  {loopAll ? "停止 Loop" : "开启 Loop"}
                </button>
              </div>
            </div>
            {realtimeError ? <div className="error-block">{realtimeError}</div> : null}
            <div className="snapshot-grid">
              <div className="snapshot-main">
                <div className="panel-subtitle">当前选中电梯详情</div>
                {selectedRealtime ? (
                  <div className="kv-grid">
                    <div>电梯 UID</div>
                    <div>{selectedRealtime.unit_uid}</div>
                    <div>电梯名称</div>
                    <div>{selectedRealtime.unit_name || "-"}</div>
                    <div>工作模式</div>
                    <div>
                      {WORK_MODE_LABELS[selectedRealtime.current_work_mode ?? ""] ||
                        selectedRealtime.current_work_mode ||
                        "-"}
                    </div>
                    <div>当前楼层</div>
                    <div>
                      {selectedRealtime.current_floor_name ||
                        selectedRealtime.current_floor_index ||
                        "-"}
                    </div>
                    <div>运行方向</div>
                    <div>
                      {DIRECTION_LABELS[selectedRealtime.current_direction_state ?? ""] ||
                        selectedRealtime.current_direction_state ||
                        "-"}
                    </div>
                    <div>门状态</div>
                    <div>
                      {DOOR_STATE_LABELS[selectedRealtime.current_door_state ?? ""] ||
                        selectedRealtime.current_door_state ||
                        "-"}
                    </div>
                    <div>在线状态</div>
                    <div>
                      {ONLINE_LABELS[selectedRealtime.online_status ?? ""] ||
                        selectedRealtime.online_status ||
                        "-"}
                    </div>
                    <div>当前速度</div>
                    <div>{selectedRealtime.current_speed ?? "-"} </div>
                    <div>机器人在梯内</div>
                    <div>{selectedRealtime.is_robot_inside ? "是" : "否"}</div>
                    <div>累计开门次数</div>
                    <div>{selectedRealtime.cum_door_open_times ?? "-"}</div>
                    <div>累计启停次数</div>
                    <div>{selectedRealtime.cum_launch_times ?? "-"}</div>
                    <div>累计移动距离</div>
                    <div>{selectedRealtime.cum_move_distance ?? "-"} m</div>
                    <div>钢丝绳弯折次数</div>
                    <div>{selectedRealtime.cum_wire_bending_times ?? "-"}</div>
                    <div>振动频率</div>
                    <div>
                      {typeof selectedRealtime.cum_vibration_frequency === "number"
                        ? VIBRATION_LABELS[selectedRealtime.cum_vibration_frequency] ??
                          selectedRealtime.cum_vibration_frequency
                        : "-"}
                    </div>
                    <div>状态时间戳</div>
                    <div>
                      {typeof selectedRealtime.timestamp === "number"
                        ? `${selectedRealtime.timestamp} (${new Date(
                            selectedRealtime.timestamp * 1000
                          ).toLocaleString()})`
                        : "-"}
                    </div>
                  </div>
                ) : (
                  <div className="empty-tip">先选中电梯并执行快照查询。</div>
                )}
              </div>
              <div className="snapshot-raw">
                <div className="panel-subtitle">最近快照原始返回</div>
                <pre>
                  {lastRealtimeEnvelope
                    ? JSON.stringify(lastRealtimeEnvelope, null, 2)
                    : "暂无数据"}
                </pre>
              </div>
            </div>
          </section>

          <section className="panel">
            <header className="section-header">
              <div>
                <div className="panel-title">电梯控制</div>
                <div className="panel-subtitle">对象：当前选中的电梯</div>
              </div>
              <div className="status-lite">
                {selectedElevator
                  ? `${selectedElevator.unit_name || "电梯"} / UID ${selectedElevator.unit_uid}`
                  : "未选中电梯"}
              </div>
            </header>
            <div className="control-grid">
              <form className="control-form" onSubmit={handleCall}>
                <div className="panel-subtitle">呼梯</div>
                <div className="input-inline compact">
                  <label htmlFor="call-floor">目标楼层</label>
                  <input
                    id="call-floor"
                    type="number"
                    min={1}
                    value={callFloorIndex}
                    onChange={(event) =>
                      setCallFloorIndex(Number.parseInt(event.target.value, 10) || 1)
                    }
                  />
                </div>
                <button
                  className="btn btn-primary"
                  disabled={!selectedUnitUID || callLoading}
                >
                  {callLoading ? "发送中..." : "呼梯到该层"}
                </button>
                {callMessage ? <div className="message-block">{callMessage}</div> : null}
              </form>

              <form className="control-form" onSubmit={handleOpenDoor}>
                <div className="panel-subtitle">开门</div>
                <div className="input-inline compact">
                  <label htmlFor="open-floor">楼层</label>
                  <input
                    id="open-floor"
                    type="number"
                    min={1}
                    value={openFloorIndex}
                    onChange={(event) =>
                      setOpenFloorIndex(Number.parseInt(event.target.value, 10) || 1)
                    }
                  />
                </div>
                <div className="input-inline compact">
                  <label htmlFor="open-duration">按压时长 (0-10s)</label>
                  <input
                    id="open-duration"
                    type="number"
                    min={0}
                    max={10}
                    value={openDuration}
                    onChange={(event) => {
                      const next = Number.parseInt(event.target.value, 10);
                      if (!Number.isFinite(next)) {
                        setOpenDuration(0);
                        return;
                      }
                      setOpenDuration(Math.max(0, Math.min(10, next)));
                    }}
                  />
                </div>
                <button
                  className="btn btn-danger"
                  disabled={!selectedUnitUID || openLoading}
                >
                  {openLoading ? "发送中..." : "执行开门"}
                </button>
                {openMessage ? <div className="message-block">{openMessage}</div> : null}
              </form>
            </div>
          </section>
        </div>
      </div>

      <section className="panel logs-panel">
        <header className="section-header">
          <div>
            <div className="panel-title">操作日志</div>
            <div className="panel-subtitle">自动转为易读日志，便于追踪行为和结果</div>
          </div>
          <button
            type="button"
            className="btn btn-subtle"
            onClick={() => historyQuery.refetch()}
            disabled={historyQuery.isFetching}
          >
            {historyQuery.isFetching ? "刷新中..." : "刷新日志"}
          </button>
        </header>
        <div className="logs-list">
          {readableLogs.length ? (
            readableLogs.map((log) => (
              <div key={log.id} className={`log-row ${log.ok ? "ok" : "err"}`}>
                <div className="log-time">{log.at}</div>
                <div className="log-main">
                  <div>{log.summary}</div>
                  <div className="log-detail">{log.detail}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-tip">暂无日志。</div>
          )}
        </div>
      </section>
    </div>
  );
}

export default App;
